<template>
  <SplitButton
    :id="id"
    :label="label"
    :icon="loading ? 'pi pi-spinner pi-spin' : icon"
    :loading="loading"
    :disabled="disabled"
    :model="items"
    :class="[
      containerClass,
      {
        'p-button-loading': loading,
        'p-disabled': disabled || loading
      }
    ]"
    :style="style"
    :type="type"
    :severity="severity"
    :raised="raised"
    :rounded="rounded"
    :text="text"
    :outlined="outlined"
    :size="computedSize"
    :aria-label="ariaLabel"
    :pt="{
      root: { class: rootClass },
      button: { class: buttonClass },
      menuButton: { class: menuButtonClass },
      menu: { class: menuClass }
    }"
    @click="onClick"
    @show="onShow"
    @hide="onHide"
  >
    <template #default>
      <slot></slot>
    </template>
  </SplitButton>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import SplitButton from 'primevue/splitbutton'
import type { SplitButtonProps, ButtonSize } from './types'
import { useLocalization } from '../../../../composables/useLocalization'

const props = withDefaults(defineProps<SplitButtonProps>(), {
  id: undefined,
  label: undefined,
  icon: undefined,
  loading: false,
  disabled: false,
  size: undefined,
  raised: false,
  rounded: false,
  text: false,
  outlined: false,
  type: 'button',
  autoZIndex: true,
  baseZIndex: 0,
  items: () => [],
  severity: undefined,
  style: undefined,
  ariaLabel: undefined
})

const emit = defineEmits<{
  (e: 'click', event: Event): void
  (e: 'show'): void
  (e: 'hide'): void
}>()

const { isRTL } = useLocalization()

const computedSize = computed<ButtonSize | undefined>(() => {
  if (props.size === 'small' || props.size === 'large') {
    return props.size
  }
  return undefined
})

const containerClass = computed(() => ({
  'p-button-sm': props.size === 'small',
  'p-button-lg': props.size === 'large',
  'p-button-loading': props.loading,
  'p-disabled': props.disabled || props.loading
}))

const rootClass = computed(() => [
  {
    'p-button-raised': props.raised,
    'p-button-rounded': props.rounded,
    'p-button-text': props.text,
    'p-button-outlined': props.outlined,
    [`p-button-${props.severity}`]: props.severity,
    'p-button-loading': props.loading,
    'p-disabled': props.disabled || props.loading
  }
])

const buttonClass = computed(() => [
  {
    'p-button-icon-only': !props.label && props.icon,
    'p-button-loading': props.loading,
    'p-disabled': props.disabled || props.loading,
    'p-button-raised': props.raised,
    'p-button-rounded': props.rounded,
    'p-button-text': props.text,
    'p-button-outlined': props.outlined,
    [`p-button-${props.severity}`]: props.severity,
    'p-button-sm': props.size === 'small',
    'p-button-lg': props.size === 'large'
  }
])

const menuButtonClass = computed(() => [
  {
    'p-disabled': props.disabled || props.loading,
    'p-button-loading': props.loading,
    'p-button-raised': props.raised,
    'p-button-rounded': props.rounded,
    'p-button-text': props.text,
    'p-button-outlined': props.outlined,
    [`p-button-${props.severity}`]: props.severity,
    'p-button-sm': props.size === 'small',
    'p-button-lg': props.size === 'large'
  }
])

const menuClass = computed(() => [
  {
    'p-rtl': isRTL,
    'p-menu-overlay': true,
    [`p-button-${props.severity}`]: props.severity
  }
])

const onClick = (event: Event) => {
  if (!props.disabled && !props.loading) {
    emit('click', event)
  }
}

const onShow = () => {
  if (!props.disabled && !props.loading) {
    emit('show')
  }
}

const onHide = () => {
  if (!props.disabled && !props.loading) {
    emit('hide')
  }
}
</script>

<style lang="scss">
.p-splitbutton {
  display: inline-flex;
  position: relative;

  &.p-button-loading {
    cursor: wait;

    .pi-spinner {
      animation: p-progress-spinner-rotate 2s linear infinite;
    }
  }

  &.p-disabled {
    cursor: default;
    opacity: 0.6;
    pointer-events: none;
  }

  &.p-button-sm {
    .p-splitbutton-defaultbutton,
    .p-splitbutton-menubutton {
      padding: 0.4375rem 0.875rem;
      font-size: 0.875rem;
    }

    .p-splitbutton-menubutton {
      padding: 0.4375rem;
    }

    .pi {
      font-size: 0.875rem;
    }
  }

  &.p-button-lg {
    .p-splitbutton-defaultbutton,
    .p-splitbutton-menubutton {
      padding: 0.75rem 1.5rem;
      font-size: 1.25rem;
    }

    .p-splitbutton-menubutton {
      padding: 0.75rem;
    }

    .pi {
      font-size: 1.25rem;
    }
  }

  &.p-button-raised {
    box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2),
                0 2px 2px 0 rgba(0, 0, 0, 0.14),
                0 1px 5px 0 rgba(0, 0, 0, 0.12);
  }

  &.p-button-rounded {
    border-radius: 2rem;

    .p-splitbutton-defaultbutton {
      border-top-left-radius: 2rem;
      border-bottom-left-radius: 2rem;
    }

    .p-splitbutton-menubutton {
      border-top-right-radius: 2rem;
      border-bottom-right-radius: 2rem;
    }
  }

  &.p-button-text {
    background-color: transparent;
    border-color: transparent;

    &:not(.p-disabled):hover {
      background: rgba(var(--primary-color), 0.04);
      border-color: transparent;
    }
  }

  &.p-button-outlined {
    background-color: transparent;
    border: 1px solid;

    &:not(.p-disabled):hover {
      background: rgba(var(--primary-color), 0.04);
    }
  }

  &[dir="rtl"] {
    .p-splitbutton-defaultbutton {
      border-radius: 0;
      border-top-right-radius: var(--border-radius);
      border-bottom-right-radius: var(--border-radius);
      border-right-width: 1px;
      border-left-width: 0;

      &:not(.p-disabled):hover {
        border-right-width: 1px;
        border-left-width: 0;
      }
    }

    .p-splitbutton-menubutton {
      border-radius: 0;
      border-top-left-radius: var(--border-radius);
      border-bottom-left-radius: var(--border-radius);

      &:not(.p-disabled):hover {
        border-right-width: 1px;
      }
    }
  }

  .p-splitbutton-defaultbutton,
  .p-splitbutton-menubutton {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: relative;
    transition: background-color 0.2s, border-color 0.2s, box-shadow 0.2s;

    &:focus-visible {
      outline: 2px solid var(--primary-500);
      outline-offset: 2px;
    }

    &.p-button-secondary:focus-visible {
      outline-color: var(--secondary-500);
    }

    &.p-button-success:focus-visible {
      outline-color: var(--green-500);
    }

    &.p-button-info:focus-visible {
      outline-color: var(--info-500);
    }

    &.p-button-warning:focus-visible {
      outline-color: var(--warning-500);
    }

    &.p-button-danger:focus-visible {
      outline-color: var(--danger-500);
    }
  }

  .p-menu {
    min-width: 100%;
  }
}

@keyframes p-progress-spinner-rotate {
  100% {
    transform: rotate(360deg);
  }
}
</style>
